import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Alert, Paper, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, TableContainer, TableHead, TableRow, TablePagination, FormControlLabel, Checkbox, IconButton, Tooltip } from '@mui/material';
import { Table as MuiTable, TableBody, TableCell } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TableViewIcon from '@mui/icons-material/TableView';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { fetchTableSchema, createTable, listTables, deleteTable, syncTableData, getTableData, updateTable, generateTableDescription } from '../../services/api';
import TabPanel from './DatabaseTabPanel';
import ExistingTables from './ExistingTables';
import AddNewTable from './AddNewTable';
import config from '../../config';

// Original Table interface with all properties
interface DatabaseTable {
  id: string;
  name: string;
  schema: any;
  source: string;
  description: string;
  createdAt: string;
  updatedAt?: string;
  rowCount?: number;
  supportsVariations?: boolean;
  sourceType?: 'woocommerce' | 'csv-upload';
  originalFileName?: string;
}

// TableType interface matching what ExistingTables expects
interface TableType {
  id?: string;
  _id?: string;
  name: string;
  description: string;
  rowCount?: number;
  supportsVariations?: boolean;
  sourceType?: 'woocommerce' | 'csv-upload';
  originalFileName?: string;
}

interface DatabaseTabProps {
  aiId: string;
  collectionId: string;
  onRefreshQnaPairs?: () => void; // Add a callback prop to refresh Q&A pairs
}

const DatabaseTab: React.FC<DatabaseTabProps> = ({ aiId, collectionId, onRefreshQnaPairs }) => {
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Tables state
  const [tables, setTables] = useState<DatabaseTable[]>([]);
  const [apiUrl, setApiUrl] = useState('https://aixue.us/wp-json/wc/v3');
  const [apiKey, setApiKey] = useState('ck_ec74138c048a4b1ad3bc4ede2addbdadd895e701');
  const [apiSecret, setApiSecret] = useState('cs_0631739534346373fa4c14cf6405bb3d40a17ee6');
  const [tableName, setTableName] = useState('');  // No default table name - let user choose
  const [tableDescription, setTableDescription] = useState('');
  const [rowLimit, setRowLimit] = useState(1000);
  const [addingTable, setAddingTable] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentTable, setCurrentTable] = useState<DatabaseTable | null>(null);
  const [tableSchema, setTableSchema] = useState<any>({});
  const [fetchingSchema, setFetchingSchema] = useState(false);
  const [syncingTable, setSyncingTable] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loadingTables, setLoadingTables] = useState(false);
  
  // Table data and pagination state
  const [tableDataDialog, setTableDataDialog] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingTableData, setLoadingTableData] = useState(false);

  // New state for custom SQL search
  const [customSql, setCustomSql] = useState('');
  const [realSql, setRealSql] = useState('');
  const [isCustomSqlMode, setIsCustomSqlMode] = useState(false);

  // New state for table description editing
  const [editDescriptionDialog, setEditDescriptionDialog] = useState(false);
  const [editingTable, setEditingTable] = useState<DatabaseTable | null>(null);
  const [newDescription, setNewDescription] = useState('');
  const [deleteWithQA, setDeleteWithQA] = useState(true); // New state for delete option
  const [generatingEditDescription, setGeneratingEditDescription] = useState(false); // AI generation in edit dialog
  
  // State for Q&A generation dialog
  const [generateQADialog, setGenerateQADialog] = useState(false);
  const [generatingQA, setGeneratingQA] = useState(false);
  const [questionPrompts, setQuestionPrompts] = useState('');
  const [qaPairsCount, setQaPairsCount] = useState<number | string>(5);
  const [generateQATable, setGenerateQATable] = useState<DatabaseTable | null>(null);
  
  // Progress tracking for Q&A generation
  const [qaProgress, setQaProgress] = useState({ generated: 0, total: 0 });
  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null);

  // Load tables when the component mounts
  useEffect(() => {
    if (aiId && collectionId) {
      loadTables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiId, collectionId]);

  // Cleanup progress interval on component unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  const loadTables = () => {
    setLoadingTables(true);
    setError(null);
    
    // Fetch tables for this AI from the backend
    listTables(aiId, collectionId)
      .then((res) => {
        if (res && res.data) {
          setTables(res.data);
        }
        setLoadingTables(false);
      })
      .catch((err) => {
        console.error('Error loading tables:', err);
        setError('Error loading tables data: ' + (err.response?.data?.error || err.message));
        setLoadingTables(false);
      });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddTable = () => {
    // Validate inputs
    if (!tableName || !apiUrl || !apiKey || !apiSecret || Object.keys(tableSchema).length === 0) {
      setError('Please provide all required fields and fetch the schema first');
      return;
    }
    
    setAddingTable(true);
    setError(null);
    
    // Create the source config object with API credentials
    const sourceConfig = {
      apiUrl: apiUrl,
      apiKey: apiKey,
      apiSecret: apiSecret
    };
    
    // Call the API to create a new table in Supabase
    createTable(aiId, {
      name: tableName,
      description: tableDescription || '',
      source: apiUrl,
      sourceConfig: sourceConfig,
      schema: tableSchema,
      rowLimit: rowLimit
    })
      .then((res) => {
        
        // Automatically sync data after manual table creation
        const newTableId = res.data.id || (res.data as any)._id;
        syncTableData(aiId, newTableId)
          .then((syncRes) => {
            // Refresh the entire table list to get updated row counts
            loadTables();
          })
          .catch((syncErr) => {
            console.error('Error syncing table after manual creation:', syncErr);
            // Still refresh the table list even if sync fails
            loadTables();
          });

        // Reset form fields
        setTableName('');
        setTableDescription('');
        setApiUrl('');
        setApiKey('');
        setApiSecret('');
        setTableSchema({});
        setRowLimit(1000);

        // Show success message
        setSuccessMessage('Database table created successfully. Initial data sync is in progress...');
        
        // Switch to the Existing Tables tab
        setTabValue(0);
      })
      .catch((err) => {
        console.error('Error creating table:', err);
        setError('Failed to create table: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setAddingTable(false);
      });
  };

  const handleCsvUpload = async (csvPreviewData: any, fileName: string) => {
    // Validate CSV data
    if (!csvPreviewData || !csvPreviewData.columns) {
      setError('Invalid CSV data. Please upload a valid CSV file.');
      return;
    }
    
    setAddingTable(true);
    setError(null);
    
    
    // For now, we'll work with the sample data but log the limitation
    if (csvPreviewData.totalRows > (csvPreviewData.sampleData?.length || 0)) {
      console.warn(`Warning: Only ${csvPreviewData.sampleData?.length || 0} out of ${csvPreviewData.totalRows} rows will be uploaded. This is a current limitation.`);
    }
    
    // Create table data for CSV upload
    const tableData = {
      name: fileName.replace('.csv', ''), // Use filename as table name (without extension)
      description: tableDescription || `CSV upload: ${fileName}`,
      source: 'csv-upload',
      sourceConfig: {}, // No API credentials needed for CSV
      schema: csvPreviewData.columns,
      csvData: csvPreviewData.sampleData || [], // Include the CSV data
      sourceType: 'csv-upload',
      originalFileName: fileName,
      collectionId: collectionId,
      totalRows: csvPreviewData.totalRows // Include total row count for reference
    };
    
    // Call the API to create CSV table
    createTable(aiId, tableData)
      .then((res) => {
        
        // Refresh the table list
        loadTables();
        
        // Reset form fields
        setTableDescription('');
        
        // Show success message
        setSuccessMessage(`CSV table "${fileName.replace('.csv', '')}" created successfully.`);
        
        // Switch to the Existing Tables tab
        setTabValue(0);
      })
      .catch((err) => {
        console.error('Error creating CSV table:', err);
        setError('Failed to create CSV table: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setAddingTable(false);
      });
  };
  const handleFetchSchema = async (selectedTables?: string[]) => {
    // Validate inputs
    if (!apiUrl || !apiKey || !apiSecret) {
      setError('Please provide API URL, key, and secret');
      return;
    }

    setFetchingSchema(true);
    setError(null);
    
    try {
      // Determine which tables to fetch - use selectedTables if provided, otherwise use tableName
      const tablesToFetch = selectedTables && selectedTables.length > 0 ? selectedTables : [tableName];
      
      // Call the API to fetch schema from WooCommerce with selected table types
      const res = await fetchTableSchema(aiId, { 
        apiUrl, 
        apiKey, 
        apiSecret,
        tableTypes: tablesToFetch,
        collectionId,
        databaseSourceType: "WooCommerce",
        rowLimit: rowLimit
      });

      // Check if the response includes created tables
      if (res.data.tables && res.data.tables.length > 0) {
        // Tables were automatically created
        // Automatically sync data after table creation and wait for completion
        await Promise.all(res.data.tables.map(async (table: DatabaseTable) => {
          const tableId = table.id || (table as any)._id;
          try {
            await syncTableData(aiId, tableId);
          } catch (syncErr) {
            console.error(`Error syncing table ${table.name}:`, syncErr);
          }
        }));
        
        // Refresh the entire table list to get updated row counts
        loadTables();

        // Show success message
        setSuccessMessage(res.data.message || 'Database tables created successfully. Data sync is in progress...');
        
        // Reset form fields
        setTableName('');
        setApiUrl('');
        setApiKey('');
        setApiSecret('');
        setTableSchema({});
        setRowLimit(1000);
        
        // Switch to the Existing Tables tab
        setTabValue(0);
      } else {
        // Only schema was returned
        setTableSchema(res.data.schemas);
      }
    } catch (err: any) {
      console.error('Error fetching schema:', err);
      setError('Failed to fetch schema: ' + (err.response?.data?.error || err.message));
    } finally {
      setFetchingSchema(false);
    }
  };
  
  const handleDeleteTable = (table: TableType) => {
    setCurrentTable(table as DatabaseTable);
    setOpenDialog(true);
  };
  
  const confirmDelete = () => {
    if (currentTable) {
      setError(null);
      
      // Get the table ID, preferring id but falling back to _id if id is undefined
      const tableId = currentTable.id || (currentTable as any)._id;
      
      if (!tableId) {
        setError('Table ID is missing. Please try refreshing the page.');
        return;
      }
      
      // Call the API to delete the table with the deleteWithQA option
      deleteTable(aiId, tableId, deleteWithQA)
        .then(() => {
          // Remove the deleted table from the state
          setTables(tables.filter(t => (t.id !== tableId && (t as any)._id !== tableId)));
          
          // Show success message
          const qaMessage = deleteWithQA ? ' and associated Q&A pairs' : '';
          setSuccessMessage(`Table "${currentTable.name}"${qaMessage} deleted successfully`);
          
          // Refresh the Q&A pairs list if Q&A pairs were deleted
          if (deleteWithQA && onRefreshQnaPairs) {
            onRefreshQnaPairs();
          }
          
          // Close the dialog
          setOpenDialog(false);
          setCurrentTable(null);
        })
        .catch((err) => {
          console.error('Error deleting table:', err);
          setError('Failed to delete table: ' + (err.response?.data?.error || err.message));
          setOpenDialog(false); // Close the dialog anyway
        });
    }
  };
  
  const handleViewTable = (table: TableType) => {
    // Set the current table and reset pagination
    setCurrentTable(table as DatabaseTable);
    setPage(0);
    setTableData([]);
    setTotalRows(0);
    setLoadingTableData(false);
    setError(null);
    setIsCustomSqlMode(false);
    setCustomSql('');
    setRealSql('');
    
    // Just open the dialog without fetching data
    setTableDataDialog(true);
  };

  const handleSyncTable = (table: TableType) => {
    // Get the table ID, preferring id but falling back to _id if id is undefined
    const tableId = table.id || (table as any)._id;
    
    if (!tableId) {
      setError('Table ID is missing. Please try refreshing the page.');
      return;
    }
    
    setSyncingTable(tableId);
    setError(null);
    
    syncTableData(aiId, tableId)
      .then((res) => {
        
        // Refresh the entire table list to get updated row counts
        loadTables();
        
        // Show success message
        setSuccessMessage(res.data.message || `Database sync completed for "${table.name}"`);
      })
      .catch((err) => {
        console.error('Error syncing table:', err);
        setError('Failed to sync table: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setSyncingTable(null);
      });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setLoadingTableData(true);
    
    if (isCustomSqlMode && realSql) {
      // When in custom SQL mode, use the cached SQL
      // Get the table ID, preferring id but falling back to _id if id is undefined
      const tableId = currentTable?.id || (currentTable as any)?._id;
      
      if (!tableId) {
        setError('Table ID is missing. Please try refreshing the page.');
        setLoadingTableData(false);
        return;
      }
      
      fetch(`${config.apiBaseUrl}/trainer/ais/${aiId}/tables/${tableId}/sql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          sqlraw: customSql, 
          page: newPage + 1, 
          pageSize: rowsPerPage,
          cachedSql: realSql // Use the cached SQL
        })
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(err => {
              throw new Error(err.error || 'Failed to execute SQL query');
            });
          }
          return response.json();
        })
        .then(data => {
          setTableData(data.data || []);
          setTotalRows(data.pagination?.totalRows || data.data?.length || 0);
        })
        .catch(err => {
          console.error('Error executing cached SQL:', err);
          setError('Failed to fetch data: ' + err.message);
        })
        .finally(() => {
          setLoadingTableData(false);
        });
    } else {
      // Normal table data view (not custom SQL)
      // Get the table ID, preferring id but falling back to _id if id is undefined
      const tableId = currentTable?.id || (currentTable as any)?._id;
      
      if (!tableId) {
        setError('Table ID is missing. Please try refreshing the page.');
        setLoadingTableData(false);
        return;
      }
      
      getTableData(aiId, tableId, newPage + 1, rowsPerPage === -1 ? 999999 : rowsPerPage)
        .then((res) => {
          setTableData(res.data.data);
        })
        .catch((err) => {
          console.error('Error fetching table data:', err);
          setError('Failed to fetch table data: ' + (err.response?.data?.error || err.message));
        })
        .finally(() => {
          setLoadingTableData(false);
        });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setLoadingTableData(true);
    
    // Fetch the data with the new page size
    getTableData(aiId, currentTable?.id as string, 1, newRowsPerPage === -1 ? 999999 : newRowsPerPage)
      .then((res) => {
        setTableData(res.data.data);
      })
      .catch((err) => {
        console.error('Error fetching table data:', err);
        setError('Failed to fetch table data: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setLoadingTableData(false);
      });
  };

  // New function to handle running custom SQL queries
  const handleRunSql = () => {
    if (!currentTable) return;
    if (!customSql.trim()) return;
    
    // Get the table ID, preferring id but falling back to _id if id is undefined
    const tableId = currentTable.id || (currentTable as any)._id;
    
    if (!tableId) {
      setError('Table ID is missing. Please try refreshing the page.');
      return;
    }
    
    setLoadingTableData(true);
    setError(null);
    setIsCustomSqlMode(true);
    
    // Call backend API to execute the SQL query
    fetch(`${config.apiBaseUrl}/trainer/ais/${aiId}/tables/${tableId}/sql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ 
        sqlraw: customSql, 
        page: page + 1, 
        pageSize: rowsPerPage === -1 ? 999999 : rowsPerPage 
      })
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            throw new Error(err.error || 'Failed to execute SQL query');
          });
        }
        return response.json();
      })
      .then(data => {
        if (data.cachedSql) {
          setRealSql(data.cachedSql); // Store the cached SQL for reuse
        }
        setTableData(data.data || []);
        setTotalRows(data.pagination?.totalRows || data.data?.length || 0);
      })
      .catch(err => {
        console.error('Error executing SQL:', err);
        setError('Failed to execute SQL query: ' + err.message);
        setIsCustomSqlMode(false);
      })
      .finally(() => {
        setLoadingTableData(false);
      });
  };
  
  // Reset SQL mode when dialog closes
  const handleCloseDataDialog = () => {
    setTableDataDialog(false);
    setIsCustomSqlMode(false);
    setCustomSql('');
    setRealSql('');
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  // Handle AI description generation for existing tables
  const handleGenerateEditDescription = async () => {
    if (!editingTable) {
      alert('No table selected for description generation.');
      return;
    }

    setGeneratingEditDescription(true);
    
    try {
      const tableId = editingTable.id || (editingTable as any)._id;
      
      if (!tableId) {
        throw new Error('Table ID is missing. Please try refreshing the page.');
      }

      
      // For existing tables, let the backend handle data sampling internally
      // This avoids sending large payloads that cause 413 errors
      const response = await generateTableDescription(aiId, tableId, null);
      const generatedDescription = response.data.description;
      
      if (!generatedDescription || generatedDescription.length < 20) {
        throw new Error('Generated description is too short or empty');
      }

      // Set the generated description
      setNewDescription(generatedDescription);
      
      
    } catch (error: any) {
      console.error('❌ Error generating AI description:', error);
      
      let errorMessage = 'Failed to generate AI description.';
      if (error.response?.data?.details) {
        errorMessage += ` ${error.response.data.details}`;
      } else if (error.message) {
        errorMessage += ` ${error.message}`;
      }
      
      alert(`${errorMessage}\n\nPlease try again or enter a description manually.`);
    } finally {
      setGeneratingEditDescription(false);
    }
  };

  const handleGenerateQA = (table: TableType) => {
    setGenerateQATable(table as DatabaseTable);
    setQuestionPrompts('');
    setQaPairsCount(5);
    setQaProgress({ generated: 0, total: 0 });
    setGenerateQADialog(true);
  };

  // Function to check Q&A generation progress
  const checkQAProgress = async (tableId: string, totalRequested: number) => {
    try {
      // Query the Q&A pairs for this table to see how many have been generated
      const response = await fetch(`${config.apiBaseUrl}/trainer/ais/${aiId}/collections/${collectionId}/qna?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Count Q&A pairs that are from this table (source contains the table name)
        const tableName = generateQATable?.name;
        const tableQAPairs = data.points?.filter((pair: any) => 
          pair.tags?.includes(`database-${tableName}`) || 
          pair.metadata?.source === `database-${tableName}`
        ) || [];
        
        setQaProgress({ generated: tableQAPairs.length, total: totalRequested });
      }
    } catch (error) {
      console.error('Error checking Q&A progress:', error);
    }
  };

  const handleSubmitGenerateQA = () => {
    if (!generateQATable || !aiId || !collectionId) return;
    
    // Get the table ID
    const tableId = generateQATable.id || (generateQATable as any)._id;
    
    if (!tableId) {
      setError('Table ID is missing. Please try refreshing the page.');
      return;
    }
    
    setGeneratingQA(true);
    setError(null);
    
    // Initialize progress tracking
    const countValue = typeof qaPairsCount === 'string' ? parseInt(qaPairsCount, 10) || 5 : qaPairsCount;
    setQaProgress({ generated: 0, total: countValue });
    
    // Start progress checking interval
    const interval = setInterval(() => {
      checkQAProgress(tableId, countValue);
    }, 15000); // Check every 15 seconds
    setProgressInterval(interval);
    
    // Call backend API to generate Q&A pairs from the database table with extended timeout
    fetch(`${config.apiBaseUrl}/trainer/ais/${aiId}/collections/${collectionId}/tables/${tableId}/generate-qa`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ 
        questionPrompts, 
        count: countValue
      }),
      // Add timeout and signal for better error handling - increased to 15 minutes
      signal: AbortSignal.timeout(900000) // 15 minutes timeout
    })
      .then(async response => {
        // Check if response is HTML (error page) instead of JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          const htmlText = await response.text();
          console.error('Received HTML response instead of JSON:', htmlText.substring(0, 200));
          throw new Error('Server returned an error page. This may be due to a timeout or server error.');
        }
        
        if (!response.ok) {
          // Try to parse JSON error, but handle cases where it's not JSON
          try {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate Q&A pairs');
          } catch (parseError) {
            // If JSON parsing fails, it might be HTML or plain text
            const errorText = await response.text();
            console.error('Non-JSON error response:', errorText.substring(0, 200));
            throw new Error(`Server error (${response.status}): Request may have timed out or encountered an internal error`);
          }
        }
        
        return response.json();
      })
      .then(data => {
        setSuccessMessage(`Successfully generated ${data.addedPairs?.length || 0} Q&A pairs from table "${generateQATable.name}"`);
        setGenerateQADialog(false);
        
        // Call the onRefreshQnaPairs callback to refresh the Q&A list
        if (onRefreshQnaPairs) {
          onRefreshQnaPairs();
        }
      })
      .catch(err => {
        console.error('Error generating Q&A:', err);
        
        // Provide more specific error messages
        if (err.name === 'AbortError' || err.message.includes('timeout')) {
          setError('Request timed out. Q&A generation is taking longer than expected. Please try with fewer Q&A pairs or check if the process completed by refreshing the page.');
        } else if (err.message.includes('Server returned an error page')) {
          setError('Server error occurred during Q&A generation. This may be due to a timeout. Please try with fewer Q&A pairs or refresh the page to check if any pairs were generated.');
        } else {
          setError('Failed to generate Q&A pairs: ' + err.message);
        }
      })
      .finally(() => {
        setGeneratingQA(false);
        // Clear the progress interval
        if (progressInterval) {
          clearInterval(progressInterval);
          setProgressInterval(null);
        }
        // Reset progress
        setQaProgress({ generated: 0, total: 0 });
      });
  };

  if (loadingTables) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Success message snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="Database management tabs"
        >
          <Tab icon={<TableViewIcon />} label="Existing Databases" id="db-tab-0" aria-controls="db-tabpanel-0" />
          <Tab icon={<AddIcon />} label="Add New Database" id="db-tab-1" aria-controls="db-tabpanel-1" />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          <ExistingTables
            tables={tables}
            syncingTable={syncingTable}
            onEditDescription={(table: TableType) => { setEditingTable(table as DatabaseTable); setNewDescription(table.description); setEditDescriptionDialog(true); }}
            onSyncTable={(table: TableType) => handleSyncTable(table)}
            onViewTable={(table: TableType) => handleViewTable(table)}
            onGenerateQA={(table: TableType) => handleGenerateQA(table)}
            onDeleteTable={(table: TableType) => handleDeleteTable(table)}
            onAddTableClick={() => setTabValue(1)}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <AddNewTable
            aiId={aiId}
            tableName={tableName}
            onTableNameChange={setTableName}
            tableDescription={tableDescription}
            onTableDescriptionChange={setTableDescription}
            apiUrl={apiUrl}
            onApiUrlChange={setApiUrl}
            apiKey={apiKey}
            onApiKeyChange={setApiKey}
            apiSecret={apiSecret}
            onApiSecretChange={setApiSecret}
            rowLimit={rowLimit}
            onRowLimitChange={setRowLimit}
            addingTable={addingTable}
            fetchingSchema={fetchingSchema}
            onFetchSchema={handleFetchSchema}
            onAddTable={handleAddTable}
            onCsvUpload={handleCsvUpload}
            tableSchema={tableSchema}
          />
        </TabPanel>

      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>Delete Table</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the table "{currentTable?.name}"? 
            This action cannot be undone and will permanently remove all data in this table.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteWithQA}
                  onChange={(e) => setDeleteWithQA(e.target.checked)}
                  color="error"
                />
              }
              label="Also delete all associated Q&A pairs"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
              {deleteWithQA 
                ? "All Q&A pairs generated from this table will be permanently deleted."
                : "Q&A pairs generated from this table will be preserved and can still be used by the chatbot."
              }
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Table Data Dialog */}
      <Dialog
        open={tableDataDialog}
        onClose={handleCloseDataDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pb: 1 }}>
          {currentTable?.name}
          {loadingTableData && <CircularProgress size={24} sx={{ ml: 2 }} />}
        </DialogTitle>
        
        {/* User query input bar at the top */}
        <Box sx={{ px: 3, pb: 2, display: 'flex', gap: 1 }}>
          <TextField
            label="Input your query"
            fullWidth
            value={customSql}
            onChange={(e) => setCustomSql(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRunSql(); }}
            disabled={loadingTableData}
            size="small"
            placeholder={`short description contains 数学`}
          />
          <Button
            variant="contained"
            onClick={handleRunSql}
            disabled={!customSql.trim() || loadingTableData}
            sx={{ minWidth: '100px' }}
          >
            Run
          </Button>
        </Box>
        
        <DialogContent sx={{ overflow: 'hidden', pt: 0 }}>
          {tableData.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  {/* Display total rows count */}
                  {rowsPerPage === -1 
                    ? `Showing all ${totalRows} records`
                    : `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRows)} of ${totalRows} records`
                  }
                </Typography>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000, { label: 'All', value: -1 }]}
                  component="div"
                  count={totalRows || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : 'more than ' + to}`
                  }
                />
              </Box>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  height: 'calc(100vh - 300px)', // Calculate height based on viewport
                  maxHeight: '500px', // Set maximum height
                  overflowY: 'auto', // Enable vertical scrolling within the table only
                  overflowX: 'auto' // Keep horizontal scrolling
                }}
              >
                <MuiTable stickyHeader aria-label="table data" size="small">
                  <TableHead>
                    <TableRow>
                      {/* Get column headers from the first row of data */}
                      {Object.keys(tableData[0]).map((column) => (
                        <TableCell key={column} sx={{minWidth: '150px'}}>
                          <Typography variant="subtitle2">{column}</Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map((row, rowIndex) => (
                      <TableRow 
                        key={rowIndex}
                        sx={{ maxHeight: '100px' }}
                      >
                        {Object.entries(row).map(([key, cell], cellIndex) => (
                          <TableCell 
                            key={`cell-${rowIndex}-${cellIndex}`}
                            sx={{
                              maxWidth: '250px', // Limit cell width
                              overflow: 'auto', // Add scrollbar for overflowing content
                              whiteSpace: 'normal', // Allow text wrapping
                              wordBreak: 'break-word' // Break long words
                            }}
                          >
                            {(() => {
                              const stringValue = typeof cell === 'object' 
                                ? JSON.stringify(cell) 
                                : String(cell);
                              
                              return stringValue.length > 200
                                ? `${stringValue.substring(0, 200000)}`
                                : stringValue;
                            })()}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </MuiTable>
              </TableContainer>
            </>
          ) : loadingTableData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography variant="body1" sx={{ py: 3, textAlign: 'center' }}>
              Please run a query to see results here.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDataDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Description Dialog */}
      <Dialog
        open={editDescriptionDialog}
        onClose={() => setEditDescriptionDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { 
            width: '100%',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>Edit Database Description</DialogTitle>
        <DialogContent
          sx={{
            overflowX: 'hidden',
            pb: 2,
            '&::-webkit-scrollbar-horizontal': {
              display: 'none'
            },
            msOverflowStyle: 'none',  /* IE and Edge */
            scrollbarWidth: 'none'  /* Firefox */
          }}
        >
          <DialogContentText sx={{ mb: 2 }}>
            Update the description for table "{editingTable?.name}".
          </DialogContentText>
          <Box sx={{ position: 'relative' }}>
            <TextField
              autoFocus
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={8}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              sx={{ 
                width: '100%',
                '& .MuiInputBase-root': {
                  width: '100%'
                }
              }}
              inputProps={{
                style: {
                  width: '100%'
                }
              }}
              InputProps={{
                endAdornment: (
                  <Tooltip title="Generate description with AI">
                    <IconButton
                      onClick={handleGenerateEditDescription}
                      disabled={generatingEditDescription}
                      sx={{ 
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' }
                      }}
                      size="small"
                    >
                      {generatingEditDescription ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <AutoAwesomeIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDescriptionDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              if (editingTable) {
                setError(null);
                
                // Get the table ID, preferring id but falling back to _id if id is undefined
                const tableId = editingTable.id || (editingTable as any)._id;
                
                if (!tableId) {
                  setError('Table ID is missing. Please try refreshing the page.');
                  return;
                }
                
                // Check console for debugging
                updateTable(aiId, tableId, { description: newDescription })
                  .then((res) => {
                    // Update the table in the state
                    setTables(tables.map(t => 
                      (t.id === tableId || (t as any)._id === tableId)
                        ? { ...t, description: newDescription } 
                        : t
                    ));
                    setSuccessMessage(`Description for "${editingTable.name}" updated successfully`);
                    setEditDescriptionDialog(false);
                  })
                  .catch((err) => {
                    console.error('Error updating table description:', err);
                    setError('Failed to update table description: ' + (err.response?.data?.error || err.message));
                  });
              }
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Q&A Dialog */}
      <Dialog
        open={generateQADialog}
        onClose={() => {
          if (!generatingQA) {
            setGenerateQADialog(false);
            // Clear progress interval if dialog is closed
            if (progressInterval) {
              clearInterval(progressInterval);
              setProgressInterval(null);
            }
            setQaProgress({ generated: 0, total: 0 });
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Generate Q&A Pairs</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Generate Q&A pairs from the table "{generateQATable?.name}".
          </DialogContentText>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Generating many Q&A pairs may take several minutes. 
              If you experience timeouts, try generating fewer pairs (1-3) at a time.
            </Typography>
          </Alert>
          
          {/* Progress indicator */}
          {generatingQA && qaProgress.total > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Progress:</strong> Generated {qaProgress.generated} out of {qaProgress.total} Q&A pairs
                {qaProgress.generated > 0 && (
                  <span> ({Math.round((qaProgress.generated / qaProgress.total) * 100)}% complete)</span>
                )}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                Progress updates every 15 seconds. Please be patient as this process can take several minutes.
              </Typography>
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Example Questions"
            fullWidth
            multiline
            rows={4}
            value={questionPrompts}
            onChange={(e) => setQuestionPrompts(e.target.value)}
            placeholder="Enter example questions user may ask"
            disabled={generatingQA}
          />
          <TextField
            margin="dense"
            label="Number of Q&A Pairs"
            type="number"
            fullWidth
            value={qaPairsCount}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setQaPairsCount(''); // Allow empty string temporarily
              } else {
                const parsed = parseInt(value, 10);
                setQaPairsCount(isNaN(parsed) ? 5 : parsed); // Default to 5 if NaN
              }
            }}
            inputProps={{ min: 1, max: 10 }}
            helperText="Recommended: 1-5 pairs to avoid timeouts"
            disabled={generatingQA}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setGenerateQADialog(false);
              // Clear progress interval
              if (progressInterval) {
                clearInterval(progressInterval);
                setProgressInterval(null);
              }
              setQaProgress({ generated: 0, total: 0 });
            }}
            disabled={generatingQA}
          >
            {generatingQA ? 'Close' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleSubmitGenerateQA} 
            color="primary" 
            variant="contained"
            disabled={generatingQA}
          >
            {generatingQA ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <span>Generating...</span>
              </Box>
            ) : (
              'Generate'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseTab;